import {
  DocumentTextIcon,
  TagIcon,
  CalendarIcon,
  UserIcon,
  PlusIcon,
  CheckIcon,
  FolderIcon,
  LinkIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';

import {
  LINE_OF_BUSINESS_LABEL,
} from '../../constants/persona';
import { contextManager } from '../../services/contextManager';
import {
  GENERAL_LINE_OF_BUSINESS,
  KnowledgeBaseService,
} from '../../services/knowledgeBaseService';
import { SearchFilters } from '../../types/search';
import { humanizeDescription } from '../../utils/humanizeDescription';

import { DocumentChunk } from './DocumentChunk';
import { SearchResultsFeedback } from './SearchResultsFeedback';
import { useSearchFeedback } from './useSearchFeedback';
import { convertDocumentsToSearchResults } from './utils';

export interface ExistingDocument {
  id: string;
  name: string;
  type: string;
  category: string;
  size: number;
  lastModified: Date;
  author: string;
  description?: string;
  tags: string[];
  path: string;
  wiki_path?: string;
  isSelected?: boolean;
  // Knowledge base search properties
  relevanceScore?: number;
  summary?: string;
  snippet?: string;
  // Uploaded document properties
  blobPath?: string;
  metadata?: Record<string, unknown>;
  source?: 'wiki' | 'guidelines' | 'user_docs';
  chunks?: DocumentChunk[];
  searchMetadata?: {
    searchScore?: number;
    rerankerScore?: number;
    searchHighlights?: unknown;
    searchCaptions?: unknown;
  };
}

interface ContextBrowserProps {
  documents: ExistingDocument[];
  selectedDocuments: string[];
  onDocumentSelect: (_documentId: string) => void;
  onDocumentDeselect: (_documentId: string) => void;
  onDocumentPreview: (_document: ExistingDocument) => void;
  onDocumentAdd?: (_selectedDocumentIds: string[]) => void;
  onSearch?: (_query: string, _filters?: SearchFilters, _projectId?: string) => void;
  isLoading?: boolean;
  searchPlaceholder?: string;
  isKnowledgeBase?: boolean;
  knowledgeBaseSource?: SourceType;
}

const SOURCE_GUIDELINES = 'guidelines';
const SOURCE_WIKI = 'wiki';
const SOURCE_USER_DOCS = 'user_docs';
type SourceType = 'wiki' | 'guidelines' | 'user_docs';
const LOB_FILTER_ALL = 'All';

const WIKI_LINE_OF_BUSINESS_OPTIONS = [
  'Trading and Supply',
  'Asset',
  'Lubricants',
  'Mobility',
] as const;
type LineOfBusiness = typeof WIKI_LINE_OF_BUSINESS_OPTIONS[number];

const CATEGORY_WIKI = 'SAHLD / DSI';
const CATEGORY_GUIDELINES = 'Guidelines & Standards';
const CATEGORY_USER_DOCS = 'User Documents';

type SortByType = 'name' | 'date' | 'category' | 'relevance';

const HIGH_RELEVANCE_LABEL = 'High Relevance';
const MEDIUM_RELEVANCE_LABEL = 'Medium Relevance';
const LOW_RELEVANCE_LABEL = 'Low Relevance';

const HIGH_RELEVANCE_CLASS = 'bg-green-100 text-green-700';
const MEDIUM_RELEVANCE_CLASS = 'bg-amber-100 text-amber-700';
const LOW_RELEVANCE_CLASS = 'bg-gray-100 text-gray-700';

interface RelevanceBadgeData {
  label: string;
  className: string;
}

const getRelevanceBadgeData = (
  index: number,
  total: number,
): RelevanceBadgeData => {
  if (total <= 2) {
    return {
      label: HIGH_RELEVANCE_LABEL,
      className: HIGH_RELEVANCE_CLASS,
    };
  }

  const percentile = (index + 1) / total;

  if (percentile <= 0.33) {
    return {
      label: HIGH_RELEVANCE_LABEL,
      className: HIGH_RELEVANCE_CLASS,
    };
  }

  if (percentile <= 0.66) {
    return {
      label: MEDIUM_RELEVANCE_LABEL,
      className: MEDIUM_RELEVANCE_CLASS,
    };
  }

  return {
    label: LOW_RELEVANCE_LABEL,
    className: LOW_RELEVANCE_CLASS,
  };
};

export const ContextBrowser: React.FC<ContextBrowserProps> = ({
  documents,
  selectedDocuments,
  onDocumentSelect,
  onDocumentDeselect,
  onDocumentAdd,
  onSearch,
  isLoading = false,
  searchPlaceholder = 'Search documents by name, type, or content...',
  isKnowledgeBase = false,
  knowledgeBaseSource = SOURCE_WIKI,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortByType>('relevance');
  const [selectedLineOfBusiness, setSelectedLineOfBusiness] = useState<Set<LineOfBusiness>>(new Set());
  const [guidelineLineOfBusinessOptions, setGuidelineLineOfBusinessOptions] =
    useState<string[]>([GENERAL_LINE_OF_BUSINESS]);
  const [selectedGuidelineLineOfBusiness, setSelectedGuidelineLineOfBusiness] =
    useState<Set<string>>(new Set());
  const [isLineOfBusinessOpen, setIsLineOfBusinessOpen] = useState(false);
  const lineOfBusinessRef = useRef<HTMLDivElement>(null);

  // Close line of business dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        lineOfBusinessRef.current &&
        !lineOfBusinessRef.current.contains(event.target as Node)
      ) {
        setIsLineOfBusinessOpen(false);
      }
    };

    if (isLineOfBusinessOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isLineOfBusinessOpen]);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const options = await KnowledgeBaseService.getLineOfBusinessOptions();
        if (!cancelled && options.length > 0) {
          setGuidelineLineOfBusinessOptions(options);
        }
      } catch {
        // Keep the default General option when choices cannot be loaded.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // Handle manual search trigger
  const canBrowseWithoutQuery =
    isKnowledgeBase && knowledgeBaseSource === SOURCE_GUIDELINES;

  const handleManualSearch = useCallback(() => {
    if (onSearch && (searchQuery.trim() || canBrowseWithoutQuery)) {
      const lineOfBusinessFilters = Array.from(selectedLineOfBusiness);
      const guidelineLineOfBusinessFilters =
        selectedGuidelineLineOfBusiness.size === 0
          ? undefined
          : Array.from(selectedGuidelineLineOfBusiness);

      const filters: SearchFilters = {
        searchSources: [knowledgeBaseSource],
        lineOfBusiness: (() => {
          if (
            knowledgeBaseSource === SOURCE_WIKI &&
            lineOfBusinessFilters.length > 0
          ) {
            return lineOfBusinessFilters;
          }
          if (knowledgeBaseSource === SOURCE_GUIDELINES) {
            return guidelineLineOfBusinessFilters;
          }
          return undefined;
        })(),
      };

      const projectId = contextManager.getContextData().project_id;
      onSearch(searchQuery.trim(), filters, projectId);
    }
  }, [
    canBrowseWithoutQuery,
    knowledgeBaseSource,
    onSearch,
    searchQuery,
    selectedGuidelineLineOfBusiness,
    selectedLineOfBusiness,
  ]);

  // Re-run the last search when the source tab or wiki filters change.
  useEffect(() => {
    if (searchQuery.trim() || canBrowseWithoutQuery) {
      handleManualSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    knowledgeBaseSource,
    selectedGuidelineLineOfBusiness,
    selectedLineOfBusiness,
  ]);

  const guidelineLineOfBusinessFilterOptions = useMemo(() => {
    const remainingOptions = guidelineLineOfBusinessOptions.filter(
      (option) => option !== GENERAL_LINE_OF_BUSINESS,
    );
    return [LOB_FILTER_ALL, GENERAL_LINE_OF_BUSINESS, ...remainingOptions];
  }, [guidelineLineOfBusinessOptions]);

  // Toggle line of business selection
  const toggleLineOfBusiness = useCallback((lob: LineOfBusiness) => {
    setSelectedLineOfBusiness((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(lob)) {
        newSet.delete(lob);
      } else {
        newSet.add(lob);
      }
      return newSet;
    });
  }, []);

  // Filter and sort documents
  const filteredDocuments = useMemo(() => {
    let filtered = documents;

    const selectedCategory = (() => {
      switch (knowledgeBaseSource) {
        case SOURCE_WIKI:
          return CATEGORY_WIKI;
        case SOURCE_GUIDELINES:
          return CATEGORY_GUIDELINES;
        case SOURCE_USER_DOCS:
          return CATEGORY_USER_DOCS;
      }
    })();

    filtered = filtered.filter((doc: ExistingDocument) =>
      doc.category === selectedCategory,
    );

    if (
      knowledgeBaseSource === SOURCE_GUIDELINES &&
      selectedGuidelineLineOfBusiness.size > 0
    ) {
      filtered = filtered.filter((doc: ExistingDocument) => {
        const documentLineOfBusiness =
          doc.chunks?.find((chunk) => chunk.sector?.trim())?.sector ??
          GENERAL_LINE_OF_BUSINESS;

        return selectedGuidelineLineOfBusiness.has(documentLineOfBusiness);
      });
    }

    return filtered;
  }, [
    documents,
    knowledgeBaseSource,
    selectedGuidelineLineOfBusiness,
  ]);

  // Use custom hook for feedback handling
  const { handleFeedback } = useSearchFeedback(filteredDocuments);

  // Handle search input changes and manual search
  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
    // Don't auto-search, wait for manual trigger
  }, []);

  const handleDocumentToggle = useCallback((document: ExistingDocument) => {
    if (selectedDocuments.includes(document.id)) {
      onDocumentDeselect(document.id);
    } else {
      onDocumentSelect(document.id);
    }
  }, [selectedDocuments, onDocumentSelect, onDocumentDeselect]);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) {
      return '0 Bytes';
    }
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const formatDate = (date: Date): string => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse">
          <div className="h-10 bg-gray-200 rounded-lg mb-4"></div>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-16 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search and Filters */}
      <div className="space-y-3">
        {/* Search Input with Button */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder={searchPlaceholder}
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleManualSearch();
                }
              }}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0097a9] focus:border-transparent"
            />
          </div>
          <button
            onClick={handleManualSearch}
            disabled={!searchQuery.trim() && !canBrowseWithoutQuery}
            className="px-4 py-2 bg-[#0097a9] text-white rounded-lg hover:bg-[#008191] disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors main-button"
          >
            Search
          </button>
        </div>

        {/* Filter Controls */}
        {isKnowledgeBase && (
          <div className="flex flex-wrap gap-3">
            {knowledgeBaseSource === SOURCE_GUIDELINES && (
              <div className="w-full">
                <div className="flex flex-wrap gap-2">
                  {guidelineLineOfBusinessFilterOptions.map((option) => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => {
                        if (option === LOB_FILTER_ALL) {
                          setSelectedGuidelineLineOfBusiness(new Set());
                          return;
                        }
                        setSelectedGuidelineLineOfBusiness((previous) => {
                          const next = new Set(previous);
                          if (next.has(option)) {
                            next.delete(option);
                          } else {
                            next.add(option);
                          }
                          return next;
                        });
                      }}
                      className={`rounded-full border px-3 py-1 text-xs font-medium ${
                        (option === LOB_FILTER_ALL &&
                          selectedGuidelineLineOfBusiness.size === 0) ||
                        selectedGuidelineLineOfBusiness.has(option)
                          ? 'border-red-500 bg-red-50 text-red-700'
                          : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {knowledgeBaseSource === SOURCE_WIKI && (
              <div className="relative min-w-48" ref={lineOfBusinessRef}>
                <button
                  onClick={() => setIsLineOfBusinessOpen(!isLineOfBusinessOpen)}
                  className={
                    'w-full px-3 py-2 border border-gray-300 rounded-lg ' +
                    'focus:ring-2 focus:ring-[#0097a9] focus:border-transparent ' +
                    'text-left flex items-center justify-between hover:bg-gray-50'
                  }
                >
                  <span className="text-sm">
                    {(() => {
                      if (selectedLineOfBusiness.size === 0) {
                        return LINE_OF_BUSINESS_LABEL;
                      }
                      if (selectedLineOfBusiness.size === 1) {
                        return Array.from(selectedLineOfBusiness)[0];
                      }
                      return `${selectedLineOfBusiness.size} selected`;
                    })()}
                  </span>
                  <ChevronDownIcon
                    className={`w-4 h-4 transition-transform ${
                      isLineOfBusinessOpen ? 'transform rotate-180' : ''
                    }`}
                  />
                </button>

                {isLineOfBusinessOpen && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
                    {WIKI_LINE_OF_BUSINESS_OPTIONS.map((lob) => (
                      <label
                        key={lob}
                        className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedLineOfBusiness.has(lob)}
                          onChange={() => toggleLineOfBusiness(lob)}
                          className="w-4 h-4 rounded border-gray-300 text-[#0097a9] focus:ring-2 focus:ring-[#0097a9]"
                        />
                        <span className="ml-2 text-sm text-gray-700">{lob}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Sort Control and Feedback Button */}
            <div className="flex gap-2 items-center">
              <select 
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0097a9] focus:border-transparent max-w-xs"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortByType)}
              >
                {isKnowledgeBase && <option value="relevance">Sort by Relevance</option>}
                <option value="date">Sort by Date</option>
                <option value="name">Sort by Name</option>
                <option value="category">Sort by Category</option>
              </select>
              
              {/* Feedback Button for Knowledge Base */}
              {isKnowledgeBase && (
                <SearchResultsFeedback
                  searchResults={convertDocumentsToSearchResults(filteredDocuments)}
                  searchQuery={searchQuery}
                  onFeedback={handleFeedback}
                />
              )}
            </div>
          </div>
        )}

        {/* Non-knowledge base filters */}
        {!isKnowledgeBase && (
          <div className="flex flex-wrap gap-3">
            <select 
              className="select select-bordered w-full max-w-xs"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortByType)}
            >
              <option value="date">Sort by Date</option>
              <option value="name">Sort by Name</option>
              <option value="category">Sort by Category</option>
            </select>
          </div>
        )}
      </div>

      {/* Results Summary */}
      <div className="flex items-center justify-between text-sm text-gray-600">
        <span>
          {filteredDocuments.length} document{filteredDocuments.length !== 1 ? 's' : ''} found
        </span>
        {selectedDocuments.length > 0 && (
          <span className="text-[#0097a9] font-medium">
            {selectedDocuments.length} selected
          </span>
        )}
      </div>

      {/* Documents List */}
      <div className="space-y-2 max-h-96 overflow-y-auto">
        {filteredDocuments.length === 0 ? (
          <div className="text-center py-8">
            <FolderIcon className="mx-auto h-12 w-12 text-gray-300" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No documents found</h3>
            <p className="mt-1 text-sm text-gray-500">
              Try adjusting your search criteria or filters.
            </p>
          </div>
        ) : (
          filteredDocuments.map((document, index) => {
            const isSelected = selectedDocuments.includes(document.id);
            const relevanceBadge = getRelevanceBadgeData(
              index,
              filteredDocuments.length,
            );
            
            return (
              <button
                key={document.id}
                className={`group w-full text-left p-4 border rounded-lg transition-all cursor-pointer ${
                  isSelected
                    ? 'border-[#0097a9] bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
                onClick={() => handleDocumentToggle(document)}
              >
                <div className="flex items-start gap-3">
                  {/* Selection Checkbox */}
                  <div className="flex-shrink-0 mt-1">
                    <div
                      className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                        isSelected
                          ? 'border-[#0097a9] bg-[#0097a9]'
                          : 'border-gray-300 group-hover:border-gray-400'
                      }`}
                    >
                      {isSelected && (
                        <CheckIcon className="w-3 h-3 text-white" />
                      )}
                    </div>
                  </div>

                  {/* Document Icon */}
                  <DocumentTextIcon className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />

                  {/* Document Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="text-sm font-medium text-gray-900 truncate">
                            {document.name}
                          </h4>
                          {/* Source Tag */}
                          {isKnowledgeBase && document.source && (
                            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                              document.source === 'wiki'
                                ? 'bg-blue-100 text-blue-700'
                                : document.source === 'user_docs'
                                  ? 'bg-pink-100 text-pink-800'
                                  : 'bg-purple-100 text-purple-700'
                            }`}>
                              {document.source === 'wiki'
                                ? 'SAHLD'
                                : document.source === 'user_docs'
                                  ? 'User Docs'
                                  : 'Guidelines'}
                            </span>
                          )}
                          {isKnowledgeBase && document.relevanceScore !== undefined && (
                            <span
                              className={`px-2 py-0.5 text-xs rounded-full font-medium ${relevanceBadge.className}`}
                            >
                              {relevanceBadge.label}
                            </span>
                          )}
                        </div>
                        
                        {/* Show summary for knowledge base, description for others */}
                        {isKnowledgeBase
                          ? humanizeDescription(
                            document.summary ?? document.description,
                          ) && (
                            <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                              {humanizeDescription(
                                document.summary ?? document.description,
                              )}
                            </p>
                          )
                          : humanizeDescription(document.description) && (
                            <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                              {humanizeDescription(document.description)}
                            </p>
                          )}
                        
                        {/* Don't show snippet for knowledge base results - only show summary */}
                      </div>
                    </div>

                    {/* Document Meta Info */}
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500 flex-wrap">
                      <div className="flex items-center gap-1">
                        <CalendarIcon className="w-3 h-3" />
                        <span>{formatDate(document.lastModified)}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <UserIcon className="w-3 h-3" />
                        <span>{document.author}</span>
                      </div>
                      <span>{formatFileSize(document.size)}</span>
                      
                      {/* Wiki Link - Inline with metadata for better visibility */}
                      {document.source === 'wiki' && document.wiki_path && (
                        <a
                          href={document.wiki_path}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-xs text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
                        >
                          <LinkIcon className="w-3 h-3" />
                          Wiki Link
                        </a>
                      )}
                    </div>

                    {/* Tags */}
                    {document.tags.length > 0 && (
                      <div className="flex items-center gap-2 mt-2">
                        <TagIcon className="w-3 h-3 text-gray-400" />
                        <div className="flex flex-wrap gap-1">
                          {document.tags.slice(0, 3).map((tag, index) => (
                            <span
                              key={index}
                              className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded"
                            >
                              {tag}
                            </span>
                          ))}
                          {document.tags.length > 3 && (
                            <span className="text-xs text-gray-500">
                              +{document.tags.length - 3} more
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>

      {/* Action Button */}
      {selectedDocuments.length > 0 && (
        <div className="pt-3 border-t border-gray-200">
          <button 
            onClick={() => onDocumentAdd?.(selectedDocuments)}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-[#0097a9] text-white rounded-lg hover:bg-[#007a8a] transition-colors main-button"
          >
            <PlusIcon className="w-4 h-4" />
            Add {selectedDocuments.length} document{selectedDocuments.length !== 1 ? 's' : ''} to context
          </button>
        </div>
      )}
    </div>
  );
};
